import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Mail } from "lucide-react";
import { isPremium } from "@/components/PremiumGate";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ProgressRing } from "@/components/ProgressRing";
import { MorningBriefing } from "@/components/MorningBriefing";
import { AREAS } from "@/lib/gardens";

const timeGreeting = () => {
    const h = new Date().getHours();
    if (h < 5) return "Peace be with you";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 21) return "Good evening";
    return "Peace be with you";
};

const stateLabel = {
    // New 5-state biblical vocabulary
    seed: "Seed",
    rooting: "Rooting",
    tender: "Tender Growth",
    forming: "Forming",
    fruitful: "Fruitfulness",
    // Legacy fallbacks so older assessments still render sensibly
    flourishing: "Fruitfulness",
    growing: "Forming",
    cultivating: "Rooting",
};

// The three biblical seasons — overlays that can arrive in any state.
// A season is only shown when the AI has genuinely named one on this area.
const seasonLabel = {
    pruning: "Pruning",
    wintering: "Wintering",
    restoration: "Restoration",
};

export default function Dashboard() {
    const { user } = useAuth();
    const [result, setResult] = useState(null);
    const [scripture, setScripture] = useState(null);
    const [encouragement, setEncouragement] = useState(null);
    const [encBusy, setEncBusy] = useState(false);
    const [habits, setHabits] = useState([]);
    const [prayers, setPrayers] = useState([]);
    const [seasonMap, setSeasonMap] = useState({}); // { areaLabel: "pruning"|"wintering"|"restoration" }
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/assessment/latest");
                setResult(r.data);
            } catch { navigate("/assessment"); return; }
            api.get("/scripture/today").then((r) => setScripture(r.data)).catch(() => {});
            api.get("/habits").then((r) => setHabits(r.data)).catch(() => {});
            api.get("/prayer-list").then((r) => setPrayers(r.data)).catch(() => {});
            // Pull the latest Blueprint to overlay any biblical Seasons the AI named per area.
            api.get("/blueprint/latest").then((res) => {
                const rep = res.data?.report || {};
                const map = {};
                for (const g of (rep.strongest_gardens || [])) {
                    const s = (g?.season || "").toLowerCase();
                    if (g?.garden && seasonLabel[s]) map[g.garden] = s;
                }
                for (const g of (rep.cultivating_gardens || [])) {
                    const s = (g?.season || "").toLowerCase();
                    if (g?.garden && seasonLabel[s]) map[g.garden] = s;
                }
                setSeasonMap(map);
            }).catch(() => {});
        })();
    }, [navigate]);

    const requestEncouragement = async () => {
        setEncBusy(true);
        try {
            const { data } = await api.post("/ai/daily-encouragement");
            setEncouragement(data.message);
        } catch { toast.error("Couldn't fetch a word for today."); }
        finally { setEncBusy(false); }
    };

    const premium = isPremium(user);
    const [weekly, setWeekly] = useState(null);
    const [wkBusy, setWkBusy] = useState(false);
    useEffect(() => {
        if (premium) api.get("/reviews/weekly/status").then((r) => setWeekly(r.data)).catch(() => {});
    }, [premium]);
    const sendWeekly = async () => {
        setWkBusy(true);
        try {
            await api.post("/reviews/weekly/send", {});
            toast.success("Your weekly review is on its way.");
            api.get("/reviews/weekly/status").then((r) => setWeekly(r.data)).catch(() => {});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't send your review.");
        } finally { setWkBusy(false); }
    };

    useEffect(() => { if (result) requestEncouragement(); /* eslint-disable-next-line */ }, [result?.assessment_id]);

    if (!result) {
        return <div className="text-muted-foreground py-24 font-serif text-2xl">Preparing your garden…</div>;
    }

    const firstName = user?.name?.split(" ")[0] || "friend";
    const priorityKeys = new Set((result.priority_gardens || []).map((p) => {
        const meta = AREAS.find((a) => a.label === p);
        return meta?.key;
    }));

    return (
        <div className="space-y-20 md:space-y-24">
            {/* Morning Briefing — Premium daily reading of the whole garden */}
            <MorningBriefing />

            {/* Greeting */}
            <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                data-testid="dashboard-greeting"
            >
                <div className="eyebrow-gold mb-4">
                    Today · {new Date().toLocaleDateString(undefined, { dateStyle: "long" })}
                </div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] font-light">
                    {timeGreeting()}, <em className="text-[hsl(var(--eden-gold))]/90">{firstName}</em>.
                </h1>
                <p className="mt-6 text-muted-foreground text-lg max-w-2xl leading-relaxed">
                    The garden God has entrusted to you. Let's tend it together today.
                </p>
            </motion.section>

            {/* Today's focus + Overall stewardship */}
            <section className="grid md:grid-cols-12 gap-6">
                <Link
                    to="/scripture"
                    data-testid="todays-focus-card"
                    className="md:col-span-7 group block hairline rounded-lg overflow-hidden hover:border-foreground/25 transition-colors ease-cultiv duration-500"
                >
                    <div className="grid grid-cols-2">
                        <div className="relative aspect-square md:aspect-auto">
                            <img
                                src="https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?w=1200&auto=format&fit=crop&q=80"
                                alt="Morning garden path"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </div>
                        <div className="p-8 md:p-10 flex flex-col justify-between">
                            <div>
                                <div className="eyebrow mb-3">Today's Focus</div>
                                {scripture ? (
                                    <>
                                        <blockquote className="font-serif text-2xl md:text-3xl leading-snug italic">
                                            &ldquo;{scripture.verse}&rdquo;
                                        </blockquote>
                                        <div className="mt-3 eyebrow-gold">{scripture.reference}</div>
                                    </>
                                ) : (
                                    <div className="text-muted-foreground">Opening today's word…</div>
                                )}
                            </div>
                            <div className="mt-6 inline-flex items-center gap-2 eyebrow group-hover:text-foreground transition-colors">
                                View reflection <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                            </div>
                        </div>
                    </div>
                </Link>

                <div className="md:col-span-5 hairline rounded-lg p-10 flex flex-col items-center justify-center text-center" data-testid="overall-stewardship-card">
                    <div className="eyebrow mb-6">Your Garden · Overall</div>
                    <ProgressRing value={result.overall_score} size={200} stroke={3} label="Cultivation" />
                    <p className="mt-8 text-sm text-muted-foreground max-w-xs leading-relaxed" data-testid="eden-score">
                        {result.encouragement}
                    </p>
                </div>
            </section>

            {/* Daily encouragement */}
            <section className="hairline rounded-lg p-10 md:p-14 relative overflow-hidden" data-testid="daily-encouragement">
                <div className="absolute -right-8 -top-8 opacity-[0.05] pointer-events-none">
                    <Sparkles className="w-64 h-64 text-[hsl(var(--eden-gold))]" strokeWidth={0.3} />
                </div>
                <div className="eyebrow-gold mb-6 flex items-center gap-2">
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} /> A word for today
                </div>
                <p className="font-serif text-3xl md:text-4xl leading-snug max-w-3xl">
                    {encouragement || (encBusy ? "Listening…" : "A quiet moment awaits.")}
                </p>
            </section>

            {/* ONE GARDEN — the interconnected areas view */}
            <section data-testid="one-garden-section">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="eyebrow mb-3">What your garden is doing today</div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">
                            The garden God has entrusted to you.
                        </h2>
                        <p className="mt-4 text-muted-foreground max-w-xl leading-relaxed">
                            Eight interconnected areas of one life. Read this list horizontally, not vertically — where
                            one area is thin, the pressure usually shows up two rows away. That is what Cultiv is here
                            to help you see.
                        </p>
                    </div>
                    <Link
                        to="/gardens"
                        data-testid="all-gardens-link"
                        className="hidden md:inline-flex items-center gap-2 eyebrow hover:text-foreground transition-colors"
                    >
                        Walk the garden <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                    </Link>
                </div>

                {/* The vine — a continuous connected list expressing ONE garden */}
                <div className="vine">
                    {result.categories.map((c, idx) => {
                        const meta = AREAS.find((a) => a.key === c.key);
                        if (!meta) return null;
                        const Icon = meta.Icon;
                        const isPriority = priorityKeys.has(c.key);
                        return (
                            <motion.div
                                key={c.key}
                                initial={{ opacity: 0, x: -8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
                                data-testid={`area-node-${c.key}`}
                                className={`vine-node is-${c.state} py-6 border-b border-foreground/8 last:border-b-0`}
                            >
                                <Link
                                    to={`/gardens#${c.key}`}
                                    className="group grid grid-cols-12 gap-4 md:gap-6 items-center"
                                >
                                    <div className="col-span-1 hidden md:flex justify-center text-[hsl(var(--eden-olive))]">
                                        <Icon className="w-5 h-5" strokeWidth={1.25} />
                                    </div>
                                    <div className="col-span-8 md:col-span-5">
                                        <div className="font-serif text-2xl md:text-3xl leading-tight">
                                            {meta.label}
                                            {isPriority && (
                                                <span className="ml-3 align-middle inline-block eyebrow-gold text-[9px]">
                                                    Priority
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-sm text-muted-foreground italic mt-1">{meta.virtue}</div>
                                    </div>
                                    <div className="col-span-2 md:col-span-3 text-right md:text-left">
                                        <div className="eyebrow flex items-center gap-2" style={{ color: "hsl(var(--eden-olive))" }}>
                                            <span>{stateLabel[c.state]}</span>
                                            {seasonMap[meta.label] && (
                                                <span
                                                    className="inline-flex items-center rounded-sm border border-[hsl(var(--eden-gold))]/45 px-1.5 py-[1px] text-[9px] tracking-[0.25em] text-[hsl(var(--eden-gold))] uppercase"
                                                    title="A biblical season Cultiv observed in this area"
                                                    data-testid={`season-badge-${c.key}`}
                                                >
                                                    · {seasonLabel[seasonMap[meta.label]]}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 h-[2px] w-full bg-foreground/10 relative overflow-hidden hidden md:block">
                                            <div
                                                className="absolute inset-y-0 left-0 bg-[hsl(var(--eden-olive))]"
                                                style={{ width: `${c.score}%` }}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-2 md:col-span-2 text-right">
                                        <div className="font-serif text-3xl md:text-4xl text-[hsl(var(--eden-forest))] dark:text-[hsl(var(--eden-cream))]">
                                            {c.score}
                                            <span className="text-sm text-muted-foreground align-top ml-0.5">/100</span>
                                        </div>
                                    </div>
                                    <div className="hidden md:flex md:col-span-1 justify-end text-muted-foreground group-hover:text-foreground transition-colors">
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.25} />
                                    </div>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            {/* Weekly review (premium) */}
            {premium && (
                <section className="hairline rounded-lg p-8 md:p-10 flex flex-wrap gap-6 items-center justify-between" data-testid="weekly-review-card">
                    <div>
                        <div className="eyebrow-gold mb-3 flex items-center gap-2">
                            <Mail className="w-3 h-3" strokeWidth={1.5} /> Weekly Stewardship Review
                        </div>
                        <div className="font-serif text-2xl md:text-3xl leading-snug max-w-xl">
                            A quiet Sunday email summary of what moved this week — sent to your inbox.
                        </div>
                        {weekly?.last_sent && (
                            <div className="mt-3 text-xs text-muted-foreground">
                                Last sent {weekly.days_since === 0 ? "today" : weekly.days_since === 1 ? "yesterday" : `${weekly.days_since} days ago`}.
                            </div>
                        )}
                    </div>
                    <Button
                        onClick={sendWeekly}
                        disabled={wkBusy}
                        data-testid="send-weekly-review-btn"
                        className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md h-11 px-6 text-xs uppercase tracking-[0.3em]"
                    >
                        {wkBusy ? "Composing…" : "Send this week's review"}
                    </Button>
                </section>
            )}

            {/* Blueprint teaser */}
            <section>
                <Link
                    to="/blueprint"
                    data-testid="blueprint-cta"
                    className="group block hairline rounded-lg overflow-hidden hover:border-foreground/25 transition-colors ease-cultiv duration-500"
                >
                    <div className="grid md:grid-cols-2">
                        <div className="p-10 md:p-16 flex flex-col justify-center">
                            <div className="eyebrow-gold mb-4">Your Blueprint</div>
                            <h3 className="font-serif text-4xl md:text-5xl leading-[1.05]">
                                A personal stewardship blueprint, composed for you.
                            </h3>
                            <p className="mt-6 text-muted-foreground max-w-md leading-relaxed">
                                A long-form assessment of the garden entrusted to you — patterns, scriptures,
                                reflection questions, a personalized prayer, and a 30-day plan. Composed after
                                considering every area of your stewardship.
                            </p>
                            <div className="mt-8 inline-flex items-center gap-2 eyebrow">
                                Read your blueprint <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                            </div>
                        </div>
                        <div className="relative min-h-[280px]">
                            <img
                                src="https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1400&auto=format&fit=crop&q=80"
                                alt="Golden hour landscape"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </Link>
            </section>

            {/* Priorities + Prayers + Habits */}
            <section className="grid md:grid-cols-3 gap-4">
                <div className="hairline rounded-lg p-8">
                    <div className="eyebrow mb-4">Three tender priorities</div>
                    <ol className="space-y-4">
                        {result.priority_gardens.map((p, i) => (
                            <li key={p} className="flex items-baseline gap-4">
                                <span className="font-serif text-3xl text-[hsl(var(--eden-gold))]/70">{String(i + 1).padStart(2, "0")}</span>
                                <span className="font-serif text-xl">{p}</span>
                            </li>
                        ))}
                    </ol>
                </div>
                <div className="hairline rounded-lg p-8">
                    <div className="flex items-center justify-between mb-4">
                        <div className="eyebrow">Prayer List</div>
                        <Link to="/prayer" className="eyebrow hover:text-foreground transition-colors">Open</Link>
                    </div>
                    {prayers.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Add your first prayer to begin.</div>
                    ) : (
                        <ul className="space-y-2">
                            {prayers.slice(0, 5).map((p) => (
                                <li key={p.item_id} className={`text-sm leading-relaxed ${p.answered ? "line-through text-muted-foreground" : ""}`}>
                                    {p.text}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="hairline rounded-lg p-8">
                    <div className="flex items-center justify-between mb-4">
                        <div className="eyebrow">Weekly Rhythms</div>
                        <Link to="/prayer" className="eyebrow hover:text-foreground transition-colors">Manage</Link>
                    </div>
                    {habits.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Cultivate a small rhythm — no streaks, only faithful presence.</div>
                    ) : (
                        <ul className="space-y-2">
                            {habits.slice(0, 5).map((h) => (
                                <li key={h.habit_id} className="text-sm flex justify-between leading-relaxed">
                                    <span>{h.name}</span>
                                    <span className="text-xs text-muted-foreground">{h.completions.length} tended</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            {/* Weekly Plan */}
            <section className="hairline rounded-lg p-10 md:p-14">
                <div className="eyebrow mb-6">This week's tending</div>
                <ul className="space-y-4 max-w-3xl">
                    {result.weekly_plan.map((step, i) => (
                        <li key={i} className="flex gap-5 items-baseline">
                            <span className="font-serif text-2xl text-[hsl(var(--eden-gold))]/70 leading-none">·</span>
                            <span className="text-lg leading-relaxed">{step}</span>
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
}
